// Grill (made-to-order) rules for PopCart: pure and shared by the API (authoritative), the
// customer app (display) and the kitchen board. Integer centavos and minutes only.
// Contract: docs/PHASE4-CONTRACT.md. Asia/Manila is UTC+8 with no DST, so wall time is a fixed offset.

const MANILA_MS = 8 * 3600 * 1000;
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const HM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export class ModifierError extends Error {
  /** @param {'missing_choice'|'too_many'|'unknown_option'|'option_unavailable'|'unknown_group'} code */
  constructor(code, groupCode, message) {
    super(message);
    this.code = code;
    this.groupCode = groupCode;
  }
}

/**
 * Validate a cart line's modifier picks against the product's groups and price them.
 * Groups the customer left empty get their default options (if the group requires a choice).
 * @param {{ code: string, name: string, minChoices: number, maxChoices: number,
 *           options: { code: string, label: string, priceCentavos: number, isDefault?: boolean, isAvailable?: boolean }[] }[]} groups
 * @param {{ group: string, options: string[] }[]} picks
 * @returns {{ chosen: { group: string, groupName: string, code: string, label: string, priceCentavos: number }[], perUnitCentavos: number }}
 */
export function priceModifiers(groups, picks = []) {
  const byGroup = new Map();
  for (const p of picks) {
    if (!groups.some((g) => g.code === p.group)) throw new ModifierError('unknown_group', p.group, 'That option is no longer on the menu.');
    byGroup.set(p.group, [...new Set(p.options || [])]);
  }
  const chosen = [];
  for (const g of groups) {
    let codes = byGroup.get(g.code);
    if (!codes || !codes.length) codes = g.minChoices > 0 ? g.options.filter((o) => o.isDefault && o.isAvailable !== false).map((o) => o.code).slice(0, g.maxChoices) : [];
    if (codes.length < g.minChoices) throw new ModifierError('missing_choice', g.code, `Choose ${g.minChoices === 1 ? 'one' : g.minChoices} for ${g.name}.`);
    if (codes.length > g.maxChoices) throw new ModifierError('too_many', g.code, `Choose up to ${g.maxChoices} for ${g.name}.`);
    for (const c of codes) {
      const o = g.options.find((x) => x.code === c);
      if (!o) throw new ModifierError('unknown_option', g.code, 'That option is no longer on the menu.');
      if (o.isAvailable === false) throw new ModifierError('option_unavailable', g.code, `${o.label} is sold out.`);
      chosen.push({ group: g.code, groupName: g.name, code: o.code, label: o.label, priceCentavos: o.priceCentavos });
    }
  }
  return { chosen, perUnitCentavos: chosen.reduce((t, c) => t + c.priceCentavos, 0) };
}

/** A stable key for cart-line identity: same product + same picks + same note = same line. */
export function modifierKey(picks = [], instructions = '') {
  const norm = [...picks].map((p) => `${p.group}:${[...(p.options || [])].sort().join('+')}`).filter((s) => !s.endsWith(':')).sort().join('|');
  return `${norm}#${String(instructions || '').trim().toLowerCase()}`;
}

/** How many of a combo can be made from component stock: min over components of floor(available / qty). */
export function comboAvailable(components) {
  if (!components.length) return 0;
  return Math.max(0, Math.min(...components.map((c) => Math.floor(c.available / c.qty))));
}

const toMin = (s) => { const m = HM_RE.exec(String(s)); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
function windowsFor(hours, day) {
  const spec = hours[day] ?? hours.daily;
  if (!Array.isArray(spec)) return null;
  if (spec.length === 2 && typeof spec[0] === 'string') return [spec];
  return spec.filter((w) => Array.isArray(w) && w.length === 2);
}

/**
 * Is the shop open at `at`? Same format as the API's vendors.hours (catalog.js):
 * {} = always open; { daily: [open, close] }; { mon: [[o,c],[o,c]], sun: [] } (missing day = daily, else closed).
 */
export function isOpenAt(hours, at) {
  if (!hours || typeof hours !== 'object' || !Object.keys(hours).length) return true;
  const w = new Date(at.getTime() + MANILA_MS);
  const list = windowsFor(hours, DAYS[w.getUTCDay()]);
  if (!list) return false;
  const t = w.getUTCHours() * 60 + w.getUTCMinutes();
  return list.some(([a, b]) => {
    const s = toMin(a), e = toMin(b);
    if (s == null || e == null) return false;
    return s <= e ? t >= s && t < e : t >= s || t < e;
  });
}

/**
 * Bookable slots: every `slotMinutes` from the first slot that leaves time to cook and ride
 * (now + prep + travel, rounded up), through the end of day `scheduleDays - 1` (Manila).
 * A slot is offered only if every shop is open at its start and none is full.
 * @param {{ now: Date, prepMinutes: number, travelMinutes: number, slotMinutes?: number, scheduleDays?: number,
 *           shops: { hours: object, booked?: Record<string, number> }[], capacity?: number }} p
 *   booked: slot ISO start → orders already booked at that shop.
 * @returns {{ startsAt: string, endsAt: string, full: boolean }[]}
 */
export function slotsFor({ now, prepMinutes, travelMinutes, slotMinutes = 15, scheduleDays = 2, shops, capacity = 6 }) {
  const step = slotMinutes * 60000;
  const earliest = now.getTime() + (prepMinutes + travelMinutes) * 60000;
  const first = Math.ceil(earliest / step) * step;
  const wallNow = new Date(now.getTime() + MANILA_MS);
  const endWall = Date.UTC(wallNow.getUTCFullYear(), wallNow.getUTCMonth(), wallNow.getUTCDate() + scheduleDays);
  const end = endWall - MANILA_MS;
  const out = [];
  for (let t = first; t + step <= end; t += step) {
    const at = new Date(t);
    if (!shops.every((s) => isOpenAt(s.hours, at))) continue;
    const iso = at.toISOString();
    const full = shops.some((s) => (s.booked?.[iso] ?? 0) >= capacity);
    out.push({ startsAt: iso, endsAt: new Date(t + step).toISOString(), full });
  }
  return out;
}

/** When a ticket should start cooking: ASAP = now; scheduled = slot − prep − travel (never before now). */
export function fireAt({ now, scheduledFor, prepMinutes, travelMinutes }) {
  if (!scheduledFor) return now;
  return new Date(Math.max(now.getTime(), scheduledFor.getTime() - (prepMinutes + travelMinutes) * 60000));
}

/** The kitchen's promised ready time: fire time + prep. */
export function readyBy({ fireAt: f, prepMinutes }) {
  return new Date(f.getTime() + prepMinutes * 60000);
}

/**
 * Rider pre-assign: a delivery may be dispatched once every live pickup on it is ready, or is
 * cooking (kitchen on_grill/packing) with ready_by within `preAssignMinutes`. Non-grill pickups
 * must be ready (Phase 2 rule).
 * @param {{ status: string, kitchenStatus: string|null, readyBy: Date|null, line: 'fresh'|'grill'|'mart'|'mixed' }[]} pickups
 */
export function dispatchable(pickups, { now, preAssignMinutes = 5 }) {
  const live = pickups.filter((p) => p.status !== 'cancelled');
  if (!live.length) return false;
  return live.every((p) => {
    if (['ready', 'picked_up', 'delivered'].includes(p.status)) return true;
    if (p.line !== 'grill' || p.status !== 'preparing' || !p.readyBy) return false;
    if (p.kitchenStatus !== 'on_grill' && p.kitchenStatus !== 'packing') return false;
    return p.readyBy.getTime() - now.getTime() <= preAssignMinutes * 60000;
  });
}

/** Ticket timer for the kitchen board: minutes left (negative = late). */
export function ticketClock({ now, readyBy: rb }) {
  if (!rb) return { minutesLeft: null, late: false };
  const left = Math.round((rb.getTime() - now.getTime()) / 60000);
  return { minutesLeft: left, late: left < 0 };
}
